import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import Footer from "@/components/Footer";
import FloatingContactBar from "@/components/FloatingContactBar";
import {
  Phone,
  MessageSquare,
  ShieldCheck,
  Clock,
  Settings,
  Thermometer,
  Box,
  Zap,
  CheckCircle2,
  Tag,
  Award,
  Users,
  MapPin,
  Plus,
  Check,
  X,
  Building2,
  Car,
  Store,
  BriefcaseBusiness,
  Presentation,
  BadgeCheck,
} from "lucide-react";

const BANNER_IMAGE =
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1778735283/Artboard_16_2x_bx5cws.webp";

const MOBILE_HERO_TOP_IMAGE =
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1778762420/Artboard_21_opt6hg.webp";

const MOBILE_APPLICATIONS_IMAGE =
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1778762419/Artboard_23_m2uec7.webp";

const MOBILE_BOTTOM_GALLERY_IMAGE =
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1778762420/Artboard_22_tpqxbo.webp";

const GALLERY_TOP = [
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735278/Artboard_3_kyk8ty.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735277/Artboard_4_cmvmez.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735278/Artboard_5_as2caw.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735278/Artboard_6_mxwdfg.webp",
];

const GALLERY_APPLICATIONS = [
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735694/Artboard_17_lpz9kr.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735694/Artboard_18_ngo8dj.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735694/Artboard_19_zi9wga.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778747453/Artboard_19_copy_vly9ek.webp",
];

const GALLERY_BOTTOM = [
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735278/Artboard_11_nlucrh.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735278/Artboard_9_pcdzcm.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735278/Artboard_8_o7ru5o.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735278/Artboard_7_ubpdyj.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735278/Artboard_12_exyvt8.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735278/Artboard_13_ghixhe.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735279/Artboard_14_c8wzt7.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778748127/Artboard_15_iggj0u.webp",
];

const partners = [
  { name: "Dubai Petroleum", style: "text-2xl md:text-4xl font-black text-white" },
  { name: "Centaur", style: "text-xl md:text-3xl font-light text-white/40" },
  { name: "Schlumberger", style: "text-2xl md:text-4xl font-bold text-white/70" },
  { name: "Emirates", style: "text-xl md:text-3xl font-medium text-red-600/80" },
  { name: "Caterpillar", style: "text-2xl md:text-4xl font-semibold text-white/50" },
];

const specs = [
  { label: "Structure", value: "High-Strength Aluminium Profile" },
  { label: "Clear Span Width", value: "5m to 60m (Customizable)" },
  { label: "Eave Height", value: "2.5m to 12.7m (Customizable)" },
  { label: "Roof Cover", value: "850 GSM White Soft PVC" },
  { label: "Side Cover", value: "PVC Fabric | Glass Wall | ABS Panel | Sandwich Panel" },
  { label: "Wind Resistance", value: "Up to 145 KPH (ASCEC-16)" },
  { label: "Comfort Options", value: "Air Conditioning, Lighting, Flooring, Partitions & Branding" },
];

const chooseUsFeatures = [
  {
    icon: BadgeCheck,
    title: "Premium Client Experience",
    desc: "Professional tent spaces designed to impress customers and support sales teams.",
  },
  {
    icon: Settings,
    title: "Customizable Layout",
    desc: "Flexible layouts for reception areas, consultation rooms, display zones, and offices.",
  },
  {
    icon: Clock,
    title: "Quick Installation",
    desc: "Fast-track deployment for launches, campaigns, exhibitions, and temporary projects.",
  },
  {
    icon: Zap,
    title: "Branding Ready",
    desc: "Exterior and interior branding options for a polished commercial appearance.",
  },
  {
    icon: Thermometer,
    title: "Air Conditioning Options",
    desc: "Cooling, insulation, lighting, and comfort features available for UAE conditions.",
  },
  {
    icon: Box,
    title: "Rental or Purchase",
    desc: "Choose flexible rental terms or long-term ownership based on your project timeline.",
  },
];

const applications = [
  {
    icon: Building2,
    title: "Real Estate Sales Centers",
    desc: "Temporary sales office tents for property launches, off-plan project sales, customer briefing centers, and real estate marketing suites.",
  },
  {
    icon: Car,
    title: "Automotive Sales Centers",
    desc: "Premium temporary showroom tents for vehicle displays, test-drive events, dealership overflow, and automotive customer engagement.",
  },
  {
    icon: Store,
    title: "Retail & Brand Activations",
    desc: "Custom sales tents for pop-up retail, product launches, roadshows, outdoor promotions, and brand campaign activations.",
  },
  {
    icon: Presentation,
    title: "Exhibition Sales Booths",
    desc: "Professional tent structures for trade shows, outdoor exhibitions, customer engagement areas, and commercial presentation spaces.",
  },
  {
    icon: BriefcaseBusiness,
    title: "Project Site Sales Offices",
    desc: "Durable sales center tents for construction sites, infrastructure projects, consultation offices, and visitor reception areas.",
  },
  {
    icon: Users,
    title: "Corporate Sales Lounges",
    desc: "Elegant temporary sales lounges for VIP clients, business meetings, presentations, and premium customer hospitality.",
  },
];

const TrustBar = () => {
  const items = [
    { icon: Award, title: "30+", sub: "Years Experience" },
    { icon: Users, title: "8000+", sub: "Projects Delivered" },
    { icon: Tag, title: "Factory Direct", sub: "Factory Pricing" },
    { icon: ShieldCheck, title: "Civil Defense", sub: "Compliant Tents" },
    { icon: MapPin, title: "UAE-Wide", sub: "Fast Installation" },
  ];

  return (
    <div className="grid grid-cols-2 md:flex md:flex-nowrap items-start md:items-center gap-4 md:gap-6 mt-6 border-t border-white/10 pt-6">
      {items.map((item, i) => (
        <div
          key={i}
          className={`flex items-start md:items-center gap-3 ${
            i === 4 ? "col-span-2 md:col-span-1" : ""
          } ${i === 3 ? "md:ml-4" : ""}`}
        >
          <div className="p-2 bg-purple-500/10 rounded-lg shrink-0">
            <item.icon className="w-5 h-5 text-[#bd6aff]" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-white font-bold text-sm leading-none whitespace-nowrap">{item.title}</span>
            <span className="text-zinc-200 text-[10px] uppercase tracking-wider font-bold mt-1.5">{item.sub}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const SalesCenter = () => {
  const navigate = useNavigate();
  const quoteRef = useRef<HTMLDivElement>(null);
  const [geo, setGeo] = useState({ code: "+971", country: "AE" });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    requirements: "",
  });
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    document.title = "Sales Center Tents UAE | Temporary Sales Office Tents Dubai | BA Tents";

    const metaDescription = document.querySelector('meta[name="description"]');

    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "BA Tents provides premium sales center tents, temporary sales office tents, and custom commercial tent structures for real estate, exhibitions, automotive, retail, and corporate projects across UAE."
      );
    }

    const mediaQuery = window.matchMedia("(min-width: 768px)");

    const handleChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
    };

    setIsDesktop(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);

    window.scrollTo(0, 0);

    const controller = new AbortController();

    const geoTimeout = setTimeout(() => {
      fetch("https://ipapi.co/json/", {
        signal: controller.signal,
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.country_calling_code) {
            const formattedCode = data.country_calling_code.startsWith("+")
              ? data.country_calling_code
              : `+${data.country_calling_code}`;

            setGeo({
              code: formattedCode,
              country: data.country_code,
            });
          }
        })
        .catch(() => {});
    }, 3000);

    return () => {
      controller.abort();
      clearTimeout(geoTimeout);
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  const handleMiniSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    navigate("/thankyou");

    const SCRIPT_URL =
      "https://script.google.com/macros/s/AKfycby5HKEZNMObZRaeTidKlOMCLR_2YpgZuP2jOscP-OHWz2pi8PT0j8giTglCwm0VO5_J/exec";

    fetch(SCRIPT_URL, {
      method: "POST",
      mode: "no-cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...formData,
        phone: `${geo.code} ${formData.phone}`,
        page: "Sales Center Tents",
      }),
    }).catch(() => {});
  };

  const scrollToQuote = useCallback(() => {
    quoteRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, []);

  return (
    <PageLayout>
      <div className="min-h-screen bg-black text-white">
        <section className="relative min-h-[60vh] md:min-h-[90vh] w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] -mt-28 md:-mt-12 overflow-hidden flex flex-col bg-black">
          <img
            src={BANNER_IMAGE}
            className="hidden md:block absolute inset-0 w-full h-full object-cover z-0 opacity-60 scale-105"
            alt="Sales Center Tents UAE"
            style={{ objectPosition: "center top" }}
            width={1920}
            height={1080}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />

          <div className="absolute inset-0 hidden md:block bg-gradient-to-b from-black/40 via-transparent to-black z-[1]" />

          <div className="flex-1 relative z-10 flex flex-col items-center justify-center px-4 md:px-0">
            <div className="w-full max-w-[1440px] grid grid-cols-1 md:grid-cols-2 gap-8 items-center md:px-12">
              <div className="w-full mb-6 p-4 md:p-0 md:-ml-12 bg-transparent">
                <div className="mt-24 inline-block px-3 py-1 rounded-full border border-white/20 bg-white/10 mb-4">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white">
                    Premium <span className="text-purple-400">Commercial Tent Solutions in UAE</span>
                  </span>
                </div>

                <h1 className="text-[32px] md:text-[58px] font-black text-white leading-[1.1] mb-5 tracking-tight uppercase">
                  Sales Center <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#bd6aff] to-purple-400">
                    & Sales Office Tents
                  </span>{" "}
                  <br />
                  <span className="text-[22px] md:text-[38px] text-white/90">in UAE & Dubai</span>
                </h1>

                <div className="mb-8 max-w-2xl">
                  <p className="hidden md:block text-zinc-300 text-sm md:text-base font-normal leading-relaxed">
                    <span className="text-white font-bold">BA Tents</span> provides premium sales center tents and temporary sales office solutions for real estate launches, exhibitions, retail activations, automotive sales, and project site offices across UAE.
                  </p>

                  <p className="block md:hidden text-zinc-300 text-sm font-normal leading-relaxed">
                    <span className="text-white font-bold">BA Tents</span> provides premium sales center tents and temporary sales office solutions for real estate, retail, automotive, exhibition, and corporate projects across UAE.
                  </p>
                </div>

                <div className="flex flex-row items-center gap-1 md:gap-2 -mt-2 mb-3 md:mb-6 w-full max-w-lg">
                  <button
                    onClick={scrollToQuote}
                    className="flex-1 py-4 bg-purple-500/10 md:bg-gradient-to-r from-[#8b31ff] to-[#bd6aff] border border-purple-400/40 md:border-0 rounded-xl text-white font-black text-[10px] md:text-xs tracking-wider flex items-center justify-center gap-1.5 uppercase backdrop-blur-md md:backdrop-blur-0 shadow-none md:shadow-[0_0_20px_rgba(139,49,255,0.3)] active:scale-95 transition-transform"
                  >
                    <Phone className="w-3.5 h-3.5 fill-black/20" />
                    <span>Get Quote</span>
                  </button>

                  <a
                    href="tel:+971588243660"
                    className="flex-1 py-4 bg-white/10 md:bg-white border border-white/20 md:border-0 rounded-xl text-white md:text-black font-black text-[10px] md:text-xs flex items-center justify-center gap-1.5 uppercase backdrop-blur-md md:backdrop-blur-0 active:scale-95 transition-transform"
                  >
                    <Phone className="w-3.5 h-3.5 fill-white md:fill-black/20 text-white md:text-black" />
                    <span>Call Now</span>
                  </a>

                  <a
                    href="https://wa.me/971588243660"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 py-4 bg-emerald-500/10 md:bg-emerald-500 border border-emerald-500/40 md:border-0 rounded-xl text-emerald-400 md:text-white font-black text-[10px] md:text-xs flex items-center justify-center gap-1.5 uppercase backdrop-blur-md md:backdrop-blur-0 active:scale-95 transition-transform"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    <span>WhatsApp</span>
                  </a>
                </div>

                <div className="max-w-lg">
                  <TrustBar />
                </div>

                {!isDesktop && (
                  <div className="relative w-full mt-8 -mb-9 overflow-hidden rounded-2xl bg-zinc-900 border border-white/10 shadow-2xl">
                    <img
                      src={MOBILE_HERO_TOP_IMAGE}
                      className="w-full h-auto object-cover"
                      alt="Sales center tents mobile gallery"
                      width={800}
                      height={1000}
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                    />
                  </div>
                )}
              </div>

              <div className="hidden md:block" />
            </div>
          </div>
        </section>

        <section className="hidden md:block mt-12 w-full max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-4 gap-1">
            {GALLERY_TOP.map((img, idx) => (
              <div key={`top-${idx}`} className="aspect-[4/3] overflow-hidden rounded-lg border border-white/10 bg-zinc-900">
                <img
                  src={img}
                  loading="lazy"
                  decoding="async"
                  width={600}
                  height={450}
                  className="w-full h-full object-cover transition-all duration-500"
                  alt={`Sales Center Gallery ${idx}`}
                />
              </div>
            ))}
          </div>
        </section>

        <section className="py-16 bg-black relative overflow-hidden border-t border-white/5">
          <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-black to-transparent z-10" />
          <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-black to-transparent z-10" />
          <div className="flex flex-col gap-4">
            <p className="text-center text-[10px] font-black text-zinc-300 uppercase tracking-[0.3em] mb-4">
              Trusted by Industry Leaders
            </p>

            <div className="flex overflow-hidden group">
              <div className="flex flex-nowrap gap-16 items-center whitespace-nowrap px-8 animate-[marquee_30s_linear_infinite]">
                {[...partners, ...partners].map((partner, idx) => (
                  <span key={idx} className={`${partner.style} tracking-tighter opacity-60 hover:opacity-100 transition-opacity uppercase`}>
                    {partner.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="pt-4 pb-8 md:py-12 max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-4 items-stretch">
            <div className="lg:col-span-2 relative p-6 md:p-8 rounded-[2rem] bg-[#121212] border border-zinc-800/60 shadow-2xl flex flex-col justify-between">
              <div>
                <h2 className="text-xl font-black mb-6 md:mb-8 uppercase tracking-tighter text-white">
                  Technical <span className="text-purple-500">Specifications</span>
                </h2>

                <table className="w-full text-left text-xs border-collapse">
                  <tbody>
                    {specs.map((s, i) => (
                      <tr key={i} className="border-b border-zinc-800/50 last:border-0">
                        <td className="py-4 font-bold text-zinc-400 uppercase text-[9px] tracking-widest w-1/3">
                          {s.label}
                        </td>
                        <td className="py-4 text-zinc-200 font-medium text-sm">
                          {s.value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div
              ref={quoteRef}
              className="relative p-6 md:p-8 rounded-3xl bg-[#0F0F0F] border-2 border-purple-900/60 shadow-[0_0_30px_rgba(139,49,255,0.15)] flex flex-col gap-4 overflow-hidden"
            >
              <div className="flex flex-col gap-1 border-b-2 border-zinc-900 pb-5">
                <h2 className="text-xl font-bold text-white tracking-tighter uppercase">Quick Quote</h2>
                <span className="text-[9px] font-black text-purple-400 uppercase tracking-widest">
                  Immediate 15-Minute Response
                </span>
              </div>

              <form className="space-y-4 pt-2" onSubmit={handleMiniSubmit}>
                <input
                  type="text"
                  placeholder="Full Name"
                  required
                  className="w-full bg-[#121212] border border-zinc-800 rounded-xl px-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />

                <input
                  type="email"
                  placeholder="Email Address"
                  required
                  className="w-full bg-[#121212] border border-zinc-800 rounded-xl px-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />

                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-purple-400 text-[10px] font-black border-r border-zinc-800 pr-3">
                    {geo.code}
                  </div>
                  <input
                    type="tel"
                    required
                    placeholder="Mobile Number"
                    className="w-full bg-[#121212] border border-zinc-800 rounded-xl pl-16 pr-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>

                <textarea
                  placeholder="Tell us about your sales center requirements..."
                  className="w-full h-28 bg-[#121212] border border-zinc-800 rounded-xl px-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none resize-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                />

                <button
                  type="submit"
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-purple-800 text-white font-black rounded-xl uppercase tracking-widest text-[11px] shadow-[0_10px_30px_rgba(139,49,255,0.2)] active:scale-95 transition-all hover:brightness-110"
                >
                  Send Request
                </button>
              </form>
            </div>
          </div>
        </section>

        <section className="pt-8 pb-16 md:py-20 max-w-7xl mx-auto px-6 border-t border-white/5">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-3xl md:text-5xl font-black tracking-tight uppercase">
              Ideal for Multiple <span className="text-[#bd6aff]">Sales Environments</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              {applications.map((app, i) => (
                <div key={i} className="flex gap-4 group">
                  <div className="mt-1">
                    <app.icon className="text-[#bd6aff] w-5 h-5 shrink-0" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-sm uppercase tracking-wide mb-1 group-hover:text-[#bd6aff] transition-colors">
                      {app.title}
                    </h3>
                    <p className="text-zinc-300 text-[11px] leading-relaxed max-w-md">{app.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden md:grid grid-cols-2 gap-3">
              {GALLERY_APPLICATIONS.map((img, idx) => (
                <div key={`app-img-${idx}`} className="relative overflow-hidden rounded-xl border border-white/5 bg-zinc-900 shadow-2xl aspect-[16/10]">
                  <img
                    src={img}
                    loading="lazy"
                    decoding="async"
                    width={600}
                    height={450}
                    className="absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-in-out hover:scale-105"
                    alt="Sales center application"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-80" />
                </div>
              ))}
            </div>

            <div className="block md:hidden overflow-hidden rounded-xl border border-white/10 bg-zinc-900 shadow-2xl">
              <img
                src={MOBILE_APPLICATIONS_IMAGE}
                className="w-full h-auto object-cover"
                alt="Sales center applications mobile gallery"
                width={800}
                height={1000}
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>

          <div className="text-center mb-12 mt-[80px]">
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-4 uppercase">
              Why Choose Our Sales Center Tents?
            </h2>
            <p className="text-zinc-300 text-sm max-w-2xl mx-auto uppercase tracking-widest">
              Professional temporary spaces for customer-facing commercial projects
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {chooseUsFeatures.map((f, i) => (
              <div key={i} className="p-6 bg-zinc-900/50 border border-white/5 rounded-2xl flex flex-col items-center text-center hover:bg-zinc-900 transition-colors group">
                <f.icon className="w-8 h-8 text-[#bd6aff] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-[11px] font-bold mb-2 tracking-tight uppercase text-white">{f.title}</h3>
                <p className="text-zinc-300 text-[10px] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="pb-24 max-w-7xl mx-auto px-2 md:px-6 space-y-2">
          <div className="hidden md:block space-y-2">
            <div className="grid grid-cols-4 gap-2">
              {GALLERY_BOTTOM.slice(0, 4).map((img, idx) => (
                <div key={`bottom-1-${idx}`} className="aspect-[4/3] overflow-hidden rounded-xl border border-white/10">
                  <img
                    src={img}
                    loading="lazy"
                    decoding="async"
                    width={600}
                    height={450}
                    className="w-full h-full object-cover transition-all duration-700"
                    alt="Sales center tent project"
                  />
                </div>
              ))}
            </div>

            <div className="grid grid-cols-4 gap-2">
              {GALLERY_BOTTOM.slice(4, 8).map((img, idx) => (
                <div key={`bottom-2-${idx}`} className="aspect-[4/3] overflow-hidden rounded-xl border border-white/10">
                  <img
                    src={img}
                    loading="lazy"
                    decoding="async"
                    width={600}
                    height={450}
                    className="w-full h-full object-cover transition-all duration-700"
                    alt="Sales center tent installation"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="block md:hidden overflow-hidden rounded-xl border border-white/10 bg-zinc-900">
            <img
              src={MOBILE_BOTTOM_GALLERY_IMAGE}
              className="w-full h-auto object-cover"
              alt="Sales center projects mobile gallery"
              width={800}
              height={1000}
              loading="lazy"
              decoding="async"
            />
          </div>
        </section>

        <section className="pb-24 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="relative group p-[1px] rounded-3xl overflow-hidden bg-white/5 border border-white/10 flex flex-col">
              <div className="bg-zinc-900/40 p-8 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Tag className="w-5 h-5 text-[#bd6aff]" />
                  </div>
                  <h2 className="text-xl font-black uppercase tracking-tighter text-white">
                    Sales Center Tent <span className="text-purple-500">Sale</span>
                  </h2>
                </div>

                <p className="text-zinc-400 text-xs leading-relaxed mb-8">
                  Direct factory pricing for long-term commercial sales centers, property launch offices, automotive display spaces, and branded customer experience areas.
                </p>

                <div className="mt-auto">
                  <ul className="space-y-3 mb-8">
                    {[
                      "5m - 60m Clear Span Widths",
                      "Custom Reception & Office Layouts",
                      "Glass Wall & Panel Options",
                      "Air Conditioning Integration",
                      "Branding & Interior Fit-Out Options",
                      "Engineered for UAE Weather Conditions",
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-[11px] text-zinc-300">
                        <CheckCircle2 className="w-4 h-4 text-[#bd6aff]" />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <button onClick={scrollToQuote} className="w-full px-6 py-4 bg-white/5 hover:bg-[#bd6aff] border border-white/10 hover:border-[#bd6aff] rounded-xl text-white text-[10px] font-black uppercase tracking-widest transition-all duration-300 flex justify-center items-center gap-2 group">
                    Get Quote <Tag className="w-3 h-3 opacity-50 group-hover:opacity-100" />
                  </button>
                </div>
              </div>
            </div>

            <div className="relative group p-[1px] rounded-3xl overflow-hidden bg-white/5 border border-white/10 flex flex-col">
              <div className="bg-zinc-900/40 p-8 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Clock className="w-5 h-5 text-[#bd6aff]" />
                  </div>
                  <h2 className="text-xl font-black uppercase tracking-tighter text-white">
                    Sales Center Tent <span className="text-purple-500">Rental</span>
                  </h2>
                </div>

                <p className="text-zinc-400 text-xs leading-relaxed mb-8">
                  Fast deployment for real estate launches, exhibitions, brand activations, dealership events, project site offices, and temporary sales campaigns.
                </p>

                <div className="mt-auto">
                  <ul className="space-y-3 mb-8">
                    {[
                      "Flexible Short-Term Rental Terms",
                      "Rapid Installation & Removal",
                      "Comfortable Client Meeting Areas",
                      "Lighting, Cooling & Flooring Options",
                      "Full Structural Maintenance",
                      "Expandable Space for Campaign Growth",
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-[11px] text-zinc-300">
                        <CheckCircle2 className="w-4 h-4 text-[#bd6aff]" />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <button onClick={scrollToQuote} className="w-full px-6 py-4 bg-white/5 hover:bg-[#bd6aff] border border-white/10 hover:border-[#bd6aff] rounded-xl text-white text-[10px] font-black uppercase tracking-widest transition-all duration-300 flex justify-center items-center gap-2 group">
                    Get Quote <Clock className="w-3 h-3 opacity-50 group-hover:opacity-100" />
                  </button>
                </div>
              </div>
            </div>

            <div className="relative group p-[1px] rounded-3xl overflow-hidden bg-gradient-to-b from-[#bd6aff]/20 to-transparent border border-[#bd6aff]/30 lg:col-span-1 md:col-span-2">
              <div className="bg-zinc-950/80 p-8 h-full flex flex-col">
                <h2 className="text-lg font-black uppercase tracking-tight text-white mb-8 text-center">
                  Rental vs Sale Comparison
                </h2>

                <div className="space-y-5">
                  <div className="grid grid-cols-3 text-[10px] font-bold uppercase tracking-widest text-zinc-300 pb-3 border-b border-white/5">
                    <div>Feature</div>
                    <div className="text-center">Rental</div>
                    <div className="text-center">Sale</div>
                  </div>

                  {[
                    { f: "Short-Term Campaigns", r: true, s: true },
                    { f: "Long-Term ROI", r: false, s: true },
                    { f: "Maintenance", r: "Incl.", s: "Opt." },
                    { f: "Lead Time", r: "Fast", s: "Custom" },
                    { f: "Asset Ownership", r: false, s: true },
                  ].map((row, i) => (
                    <div key={i} className="grid grid-cols-3 items-center py-4 border-t border-white/5">
                      <div className="text-[10px] font-bold text-zinc-300 uppercase leading-none">{row.f}</div>
                      <div className="flex justify-center">
                        {row.r === true ? <Check className="w-4 h-4 text-[#bd6aff]" /> : row.r === false ? <X className="w-4 h-4 text-zinc-600" /> : <span className="text-[9px] text-[#bd6aff] font-bold uppercase">{row.r}</span>}
                      </div>
                      <div className="flex justify-center">
                        {row.s === true ? <Check className="w-4 h-4 text-[#bd6aff]" /> : row.s === false ? <X className="w-4 h-4 text-zinc-600" /> : <span className="text-[9px] text-[#bd6aff] font-bold uppercase">{row.s}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="pb-32 max-w-5xl mx-auto px-6 relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-600/5 blur-[120px] rounded-full pointer-events-none" />

          <div className="text-center mb-12 relative z-10">
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-4 uppercase text-white">
              Sales Center Tent <span className="text-[#bd6aff]">FAQ</span>
            </h2>
            <p className="text-zinc-300 text-[10px] uppercase tracking-[0.3em]">
              Expert Commercial Tent Insights
            </p>
          </div>

          <div className="space-y-4 relative z-10">
            {[
              {
                q: "Do you provide sales center tents on rental basis in UAE?",
                a: "Yes. BA Tents provides sales center tent rental solutions for real estate, automotive, retail, exhibition, and corporate projects across UAE.",
              },
              {
                q: "Do you offer sales center tents for sale in UAE?",
                a: "Yes. BA Tents manufactures and supplies sales center tents for long-term commercial use, property launches, branded sales offices, automotive display areas, and customer service spaces.",
              },
              {
                q: "Can a sales center tent be customized with branding and interiors?",
                a: "Yes. Customization options include branded exterior graphics, glass wall systems, ABS or sandwich panels, flooring, lighting, partitions, reception areas, meeting rooms, and air conditioning.",
              },
              {
                q: "Are sales center tents suitable for real estate project launches?",
                a: "Yes. Sales center tents are ideal for real estate developers who need a temporary but premium customer-facing space for off-plan launches, site visits, client meetings, and project presentations.",
              },
              {
                q: "Do you provide installation and removal services?",
                a: "Yes. BA Tents provides complete delivery, installation, dismantling, relocation, and maintenance support for sales center tents across Dubai, Abu Dhabi, Sharjah, and all other Emirates.",
              },
              {
                q: "Can sales center tents be air-conditioned?",
                a: "Yes. Our sales center tents can be equipped with air conditioning, insulation, ventilation, lighting, and comfortable interior layouts for UAE weather conditions.",
              },
            ].map((item, idx) => (
              <div key={idx} className="group p-[1px] rounded-2xl bg-white/10 border border-white/5 hover:border-[#bd6aff]/40 transition-all duration-300 shadow-xl">
                <details className="group">
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <span className="text-sm font-bold text-zinc-200 uppercase tracking-tight group-hover:text-white transition-colors">
                      {item.q}
                    </span>
                    <Plus className="w-4 h-4 text-[#bd6aff] group-open:rotate-45 transition-transform duration-300" />
                  </summary>
                  <div className="px-6 pb-6 text-zinc-400 text-xs leading-relaxed border-t border-white/5 pt-4">
                    {item.a}
                  </div>
                </details>
              </div>
            ))}
          </div>
        </section>
      </div>

      <FloatingContactBar onQuoteClick={scrollToQuote} />
      <Footer />
    </PageLayout>
  );
};

export default SalesCenter;
